import { Injectable } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { SessionStorageService } from '../session-storage.service';
import { LocalStorageKey } from '../models/storageKey';

const UTM_PARAM_TO_KEY: { [param: string]: LocalStorageKey } = {
  utm_source: LocalStorageKey.UTM_SOURCE,
  utm_medium: LocalStorageKey.UTM_MEDIUM,
  utm_campaign: LocalStorageKey.UTM_CAMPAIGN,
  utm_term: LocalStorageKey.UTM_TERM,
  utm_content: LocalStorageKey.UTM_CONTENT
};

/**
 * Captures UTM campaign parameters from the landing URL and persists them as
 * first-touch attribution. Must be constructed during app bootstrap — before
 * the router's initial navigation rewrites the URL and drops the query string
 * (FunnelTrackingService injects it, which is early enough).
 *
 * GA session attribution happens on its own via gtag; this exists so the
 * attribution also lands in our own storage (localStorage + the Firestore
 * signupSessions doc) and survives multi-day resumes.
 */
@Injectable({
  providedIn: 'root'
})
export class AttributionService {
  constructor(
    private analytics: AnalyticsService,
    private sessionStorage: SessionStorageService
  ) {
    this.captureFromUrl(window.location.search);

    const attribution = this.getAttribution();
    if (Object.keys(attribution).length > 0) {
      this.analytics.setUserProperties(attribution);
    }
  }

  /**
   * Attribution as GA-style params (utm_source, utm_medium, ...), only the
   * ones that were present on first touch.
   */
  getAttribution(): { [param: string]: string } {
    const attribution: { [param: string]: string } = {};
    for (const [param, key] of Object.entries(UTM_PARAM_TO_KEY)) {
      const value = localStorage.getItem(key);
      if (value) {
        attribution[param] = value;
      }
    }
    return attribution;
  }

  /**
   * Re-writes stored attribution through SessionStorageService so it mirrors
   * to Firestore. Called after the Firestore connection is established —
   * capture happens at bootstrap, before Firestore is available.
   */
  persistToSession(): void {
    for (const key of Object.values(UTM_PARAM_TO_KEY)) {
      const value = localStorage.getItem(key);
      if (value) {
        this.sessionStorage.setItem(key, value);
      }
    }
  }

  private captureFromUrl(search: string): void {
    if (!search) return;

    // First-touch only: a later visit (e.g. a direct return to finish signup)
    // must not overwrite the campaign that originally brought the user in.
    const alreadyCaptured = Object.values(UTM_PARAM_TO_KEY)
      .some(key => localStorage.getItem(key));
    if (alreadyCaptured) return;

    const params = new URLSearchParams(search);
    let captured = false;
    for (const [param, key] of Object.entries(UTM_PARAM_TO_KEY)) {
      const value = params.get(param);
      if (value) {
        // Cap length defensively; these come straight from the URL.
        localStorage.setItem(key, value.slice(0, 100));
        captured = true;
      }
    }

    if (captured) {
      this.analytics.trackEvent('campaign_attributed', this.getAttribution());
    }
  }
}
